import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MatTree, MatTreeNodeDef, MatTreeNode, MatNestedTreeNode, MatTreeNodeToggle, MatTreeNodeOutlet } from '@angular/material/tree';
import { Subject } from 'rxjs';
import { orderBy, groupBy } from 'lodash-es';
import { OrderUtilityService } from '../../../../services';
import { OrderControllerService } from '../../../../services/order/order-controller.service';
import { OrderShipmentGroup, OrderShipment } from '../../../../models';
import { OrderStatusNode } from '../../models/interfaces';
import { LineStatus } from '../../../../models/enums';
import { takeUntil } from 'rxjs/operators';
import { RouterLink } from '@angular/router';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { LoadingSpinnerComponent } from '../../../../shared/components/loading-spinner/loading-spinner.component';

@Component({
  selector: 'insight-orders-by-status',
  templateUrl: './orders-by-status.component.html',
  styleUrls: ['./orders-by-status.component.scss', '../../scss/operational-dashboard-shared.scss'],
  imports: [RouterLink, MatButton, MatIcon, LoadingSpinnerComponent, MatTree, MatTreeNodeDef, MatTreeNode, MatIconButton, MatNestedTreeNode, MatTreeNodeToggle, MatTreeNodeOutlet]
})
export class OrdersByStatusComponent implements OnInit, OnDestroy {
  public nodeDataSource: OrderStatusNode[] = [];
  public childrenAccessor = (node: OrderStatusNode) => node.children ?? [];
  public isLoading = true;
  public didError = false;

  @ViewChild(MatTree) private tree: MatTree<OrderStatusNode>;

  private unsubscribeTrigger$ = new Subject();

  constructor(
    private orderControllerService: OrderControllerService,
    private orderUtilityService: OrderUtilityService) { }

  ngOnInit(): void {
    this.orderControllerService.orderDataRefreshedEvent
      .pipe(takeUntil(this.unsubscribeTrigger$))
      .subscribe(() => {
        if (this.isLoading || this.orderControllerService.orderDataNeedsRefresh()) {
          const sortedShipments = orderBy(this.orderControllerService.getAllOrderShipments().orderShipmentGroups, 'status', 'asc');
          this.nodeDataSource = this.generateNodeData(sortedShipments);
          this.isLoading = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribeTrigger$.next();
    this.unsubscribeTrigger$.complete();
  }

  public collapseAll() {
    this.tree.collapseAll();
  }

  public expandAll() {
    this.tree.expandAll();
  }

  public hasChild(notUsed: number, node: OrderStatusNode): boolean {
    return !!node.children && node.children.length > 0;
  }

  public hasData(): boolean {
    return this.nodeDataSource.length > 0;
  }

  private generateNodeData(orderShipmentGroups: OrderShipmentGroup[]): OrderStatusNode[] {
    const nodes: OrderStatusNode[] = [];
    orderShipmentGroups.forEach(group => {
      if (group.status !== LineStatus.Complete) {
        nodes.push(this.createNodeFromOrderShipmentGroup(group));
      }
    });

    return nodes;
  }

  private createNodeFromOrderShipmentGroup(group: OrderShipmentGroup): OrderStatusNode {
    const statusParam = { status: group.status, errorsOnly: false };
    const node: OrderStatusNode = {
      name: 'Orders in ' + this.orderUtilityService.mapOrderShipmentStatus(group.status),
      nodeMatchesCount: group.orderShipments.length,
      queryParams: statusParam,
      children: this.createOrderTypeNodes(group.orderShipments, statusParam)
    };

    return node;
  }

  private createOrderTypeNodes(orderShipments: OrderShipment[], queryParam: any): OrderStatusNode[] {
    const orderStatusNodes: OrderStatusNode[] = [];
    const orderTypeGroups = groupBy(orderShipments, 'orderType');

    Object.keys(orderTypeGroups).forEach(key => {
      const statusAndOrderParams = { status: queryParam.status, orderType: key, errorsOnly: false };
      const node: OrderStatusNode = {
        name: this.orderUtilityService.mapOrderOrderType(parseInt(key, 10)),
        nodeMatchesCount: orderTypeGroups[key].length,
        queryParams: statusAndOrderParams,
        children: this.createMediaTypeNodes(orderTypeGroups[key], statusAndOrderParams)
      };
      orderStatusNodes.push(node);
    });

    return orderStatusNodes;
  }

  private createMediaTypeNodes(orderShipments: OrderShipment[], queryParams: any): OrderStatusNode[] {
    const orderStatusNodes: OrderStatusNode[] = [];
    const mediaTypeGroups = groupBy(orderShipments, 'mediaType');

    Object.keys(mediaTypeGroups).forEach(key => {
      const statusOrderAndMediaParams = { status: queryParams.status, orderType: queryParams.orderType, mediaType: key, errorsOnly: false };
      const node: OrderStatusNode = {
        name: this.orderUtilityService.mapOrderMediaType(parseInt(key, 10)),
        nodeMatchesCount: mediaTypeGroups[key].length,
        queryParams: statusOrderAndMediaParams,
      };
      orderStatusNodes.push(node);
    });

    return orderStatusNodes;
  }
}
